import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import relativeTime from 'dayjs/plugin/relativeTime';
import { parseServerDate } from 'library/helpers/dateFunctions';
import { isActiveBooking } from 'library/helpers/lockerHelpers';
import RenderStatusTag from 'zComponents/atoms/RenderStatusTag';

dayjs.extend(duration);
dayjs.extend(isBetween);
dayjs.extend(relativeTime);

export const getCurrentTime = () => {
	const currentDate = dayjs();
	const formattedTime = currentDate.format('HH:mm');
	return formattedTime;
};

export const formatDates = (dateStr) => {
	const date = dayjs(dateStr);
	return date.format('ddd, D MMM');
};

export function isDateInFuture(date) {
	const today = dayjs();
	return dayjs(date).isAfter(today);
}

export function currentAcceptableDate() {
	return dayjs();
}

const calculateBookingStatus = (startTime, endTime, deletedDate, isFilter, bookingStatusType) => {
	const startTime2 = dayjs(startTime).local();
	const endTime2 = dayjs(endTime).local();

	switch (true) {
		case deletedDate !== null:
			return isFilter ? bookingStatusType[4].title : RenderStatusTag(bookingStatusType[4].title, 'red');
		case dayjs().isBefore(startTime2):
			return isFilter ? bookingStatusType[2].title : RenderStatusTag(bookingStatusType[2].title, 'gold');
		case dayjs().isBetween(startTime2, endTime2):
			return isFilter ? bookingStatusType[1].title : RenderStatusTag(bookingStatusType[1].title, 'green');
		case dayjs().isAfter(endTime2):
			return isFilter ? bookingStatusType[3].title : RenderStatusTag(bookingStatusType[3].title, 'blue');
		default:
			return isFilter ? bookingStatusType[5].title : RenderStatusTag(bookingStatusType[5].title);
	}
};

// Helper function to get the last login date
export const getLastLogin = (lastLogin) => {
	if (!lastLogin) return '-';

	// Get the last login date  and convert to local timezone for comparison
	const lastLoginDate = dayjs(lastLogin).local();

	// Get today and yesterday in the local timezone
	const today = dayjs().startOf('day');
	const yesterday = dayjs().subtract(1, 'day').startOf('day');

	if (lastLoginDate.isSame(today, 'day')) {
		return 'Today';
	}

	if (lastLoginDate.isSame(yesterday, 'day')) {
		return 'Yesterday';
	}

	//If is not today or yesterday, format the date as D/MM/YY
	return lastLoginDate.format('D/MM/YY');
};

/**
 * The booking a person is holding RIGHT NOW — started, not ended, not
 * deleted. The People views previously used the person's FIRST booking of any
 * state, so someone booked for next week showed "Current Booking: … N days
 * remaining" while the locker tile (correctly) showed Available.
 */
export const getCurrentBookingForPerson = (bookings = [], personID, now = new Date()) => {
	const active = bookings.filter((booking) => booking.assignedToPerson === personID && isActiveBooking(booking, now));
	if (!active.length) return null;
	// Latest startTime wins on overlap — same determinism rule as
	// getActiveBookingForDevice, instead of letting array order decide
	return active.sort((a, b) => new Date(b.startTime) - new Date(a.startTime))[0];
};

// "<device> - <category>" label from already-resolved entities
export const formatBookingLabel = (device, category) =>
	device ? (category ? `${device.name} - ${category.name}` : device.name) : '';

// Same label resolved through the device→category chain (one implementation —
// this was inlined four times in PeopleTableOverview alone)
export const getPersonBookingLabel = ({ booking, devices = [], categories = [] }) => {
	if (!booking) return '';
	const device = devices.find((d) => d.deviceID === booking.deviceID);
	const category = device && categories.find((c) => c.categoryID === device.categoryID);
	return formatBookingLabel(device, category);
};

// Helper function to calculate remaining duration of a single booking
export const getRemainingDuration = (booking) => {
	if (!booking?.endTime) return '-';

	return dayjs.duration(dayjs(booking.endTime).diff(dayjs())).humanize().replace(/^an? /, '1 ');
};

export const filterPeopleByFilters = (people, bookingsData, logsData, filters) => {
	if (filters.length === 0) return people;

	return people.filter((person) => {
		const personBookings = bookingsData.filter((b) => b.assignedToPerson === person.personID);
		const personLogs = logsData.filter((l) => l.personID === person.personID);

		// check if person matches ANY of the selected filters
		return filters.some((filter) => {
			return filter.filter(person, personBookings, personLogs);
		});
	});
};

export const getBookingState = (booking) => {
	const currentTime = dayjs();
	const startTime = parseServerDate(booking.startTime);
	const endTime = parseServerDate(booking.endTime);

	if (currentTime.isBefore(startTime)) {
		return 'Upcoming';
	}

	if (currentTime.isBetween(startTime, endTime)) {
		return 'Active';
	}

	return 'Expired';
};

export default calculateBookingStatus;
